import { DATA_POINTS, TYPES, SCHEMA_ORG, PEOPLE_AUDIENCE } from './constants';
import { buildBrandFromProduct } from './Schema';
import { asGtin, buildOffer, getVariantUrl } from './OfferSchema';
import { buildAggregateOffer } from './AggregateOfferSchema';
import { buildYotpoRating } from './AggregateRatingSchema';
import {
  buildVariesBy,
  getAdditionalVariantProperties,
  getMappedVariantAttributes,
} from './variantOptionMap';
import { fromGid } from '@/lib/shopify/gid';

function buildAggregateRatingFromProductRating(productRating) {
  return productRating ? buildYotpoRating(productRating) : undefined;
}

function resolveProductGroupID(product) {
  const numericId = fromGid(product?.id);
  if (numericId) return numericId;
  return product?.handle || undefined;
}

// Builders for ProductGroup and standard Product schemas
export function buildVariantProduct(variant, product, siteSettings, variantPrice, slug, options = {}) {
  if (!variant) return null;
  const { isSingleProduct = false, metadata, imageUrls, productRating, productReviews } = options;

  const { color, size, material, pattern } = getMappedVariantAttributes(variant.selectedOptions);
  const additionalProperties = getAdditionalVariantProperties(variant.selectedOptions);

  const url = isSingleProduct
    ? `${DATA_POINTS.url.product}/${slug}`
    : getVariantUrl(variant, slug);

  const id = isSingleProduct
    ? `${DATA_POINTS.url.product}/${slug}`
    : `${DATA_POINTS.url.product}/${slug}#variant-${fromGid(variant.id) || variant.id}`;

  const variantNameSuffix = variant.selectedOptions
    ?.map((o) => o.value)
    .filter((val) => val && val.toLowerCase() !== 'default title')
    .join(' / ') || '';
  const name = isSingleProduct
    ? (product.title || '')
    : `${product.title}${variantNameSuffix ? ` — ${variantNameSuffix}` : ''}`;

  const description = product.seo?.description || metadata?.description || undefined;

  const variantImage = variant.image?.url || imageUrls?.[0];

  const aggregateRating = buildAggregateRatingFromProductRating(productRating);

  return {
    '@type': TYPES.product,
    '@id': id,
    name,
    url,
    ...(description && { description }),
    ...(variantImage && { image: variantImage }),
    sku: variant.sku,
    ...(asGtin(variant.barcode) && { gtin: asGtin(variant.barcode) }),
    brand: buildBrandFromProduct(),
    ...(color && { color }),
    ...(size && {
      size: {
        '@type': TYPES.size_specification,
        name: size,
      },
    }),
    ...(material && { material }),
    ...(pattern && { pattern }),
    ...(additionalProperties.length > 0 && {
      additionalProperty: additionalProperties,
    }),
    ...(variant.productWeight?.value && {
      weight: {
        '@type': TYPES.quantitativeValue,
        value: variant.productWeight.value,
        unitCode: 'LBR',
      },
    }),
    itemCondition: SCHEMA_ORG.newCondition,
    offers: buildOffer(variant, product, { [variant.id]: variantPrice }, slug, siteSettings, {
      includeShipping: true,
      includeReturnPolicy: true,
      includePriceSpec: true,
    }),
    ...(aggregateRating && { aggregateRating }),
    ...(isSingleProduct && productReviews?.length && productRating && { review: productReviews }),
    ...(!isSingleProduct && {
      isVariantOf: {
        '@id': `${DATA_POINTS.url.product}/${slug}#productgroup`,
      },
    }),
  };
}


export function buildProductGroup({
  product,
  slug,
  metadata,
  variantPrices,
  imageUrls,
  siteSettings,
  productRating,
  productReviews,
  productDescription,
}) {
  if (!product || !product.variants?.length) return undefined;

  // Auto-derive varying attributes from the options
  const optionMap = {};
  const hasVariant = [];

  product.variants.forEach((variant) => {
    variant.selectedOptions?.forEach((opt) => {
      if (!optionMap[opt.name]) optionMap[opt.name] = new Set();
      optionMap[opt.name].add(opt.value);
    });

    const variantSchema = buildVariantProduct(variant, product, siteSettings, variantPrices[variant.id], slug, {
      metadata,
      imageUrls,
      productRating,
      productReviews,
    });
    if (variantSchema) {
      hasVariant.push(variantSchema);
    }
  });

  if (hasVariant.length === 0) return undefined;

  const variesBy = buildVariesBy(optionMap);
  const aggregateRating = buildAggregateRatingFromProductRating(productRating);

  return {
    '@type': TYPES.product_group,
    '@id': `${DATA_POINTS.url.product}/${slug}#productgroup`,
    name: product.title || metadata?.title || '',
    description: productDescription || product.seo?.description || metadata?.description || undefined,
    url: `${DATA_POINTS.url.product}/${slug}`,
    mainEntityOfPage: {
      '@id': `${DATA_POINTS.url.product}/${slug}#webpage`,
    },
    productGroupID: resolveProductGroupID(product),
    sku: product.variants[0]?.sku, // fallback group sku to first variant
    brand: buildBrandFromProduct(),
    category: product.productType || undefined,
    audience: PEOPLE_AUDIENCE,
    ...(aggregateRating && { aggregateRating }),
    ...(productReviews?.length && productRating && { review: productReviews }),
    ...(variesBy.length > 0 && { variesBy }),
    ...(imageUrls?.length > 0 && { image: imageUrls }),
    offers: buildAggregateOffer(product, slug, siteSettings, variantPrices),
    hasVariant,
  };
}
